import { Message } from "google-protobuf"
import type { Site, Equipment } from "../generated/proto-ts/main"
import { uuidToPbUUID } from "./utils"
import type { IEN1176AnswerMap } from "../types"
import type { UUID } from "../generated/proto-ts/main"
import { pbUUIDToUuid } from "./utils"
import { CatalogTemplate, ResolvedCatalog, TemplateDetail } from "../types/regulatory"
import {
  ECloudRegulatoryControlPointCategory,
  ECloudRegulatoryDomain,
  ECloudRegulatoryReferenceType,
  ICloudRegulatoryControlPoint,
  ICloudRegulatoryControlTemplate,
} from "../types"
// Compatibility is limited to presentation. Revisions never become answer UUIDs.
export function presentTemplate(
  template: CatalogTemplate,
  detail?: TemplateDetail,
  resolved?: ResolvedCatalog,
): ICloudRegulatoryControlTemplate {
  const scope = detail?.revision.definition.scope ?? template.scope
  return {
    UUID: template.uuid,
    EnterpriseUUID: template.enterpriseUuid,
    Name: detail?.revision.definition.name ?? template.name,
    Description: detail?.revision.definition.description ?? "",
    Domain: scope === "site" ? ECloudRegulatoryDomain.Site : ECloudRegulatoryDomain.Equipment,
    Scope: scope,
    RevisionUUID: detail?.revision.uuid ?? template.currentRevisionUuid ?? undefined,
    Definition: detail?.revision.definition,
    ControlPointUUIDs: resolved?.controls.map((c) => c.point.pointUuid) ?? null,
  }
}
export function presentControls(catalog: ResolvedCatalog): ICloudRegulatoryControlPoint[] {
  const modules = new Map(
    catalog.templates.map((t) => [t.revisionUuid, `${t.name} · v${t.sourceVersion}`]),
  )
  return catalog.controls.map(({ point, revisionUuid }) => {
    const p = point.definition
    const referenceUuid = point.source?.referenceEditionUuid ?? p.reference
    return {
      UUID: point.pointUuid,
      ID: p.controlCode || p.id,
      Text: p.label,
      ParentID: "",
      DisplayCategory: catalog.presentation?.categories.find(
        (c) => c.code === catalog.presentation?.assignments[point.uuid],
      ),
      ClassificationReviewed: catalog.presentation?.reviewed,
      ClassificationProfileUUID: catalog.presentation?.uuid,
      Category:
        catalog.presentation?.categories.find(
          (c) => c.code === catalog.presentation?.assignments[point.uuid],
        )?.legacyCode ?? ECloudRegulatoryControlPointCategory.Unknown,
      ReferenceUUID: referenceUuid,
      ReferenceSection: point.source?.clause ?? p.sourceClause ?? p.reference,
      Reference: {
        UUID: referenceUuid,
        Name: p.reference,
        Title: "",
        Type: ECloudRegulatoryReferenceType.Unknown,
        SpecificEquipmentType: "",
      },
      Definition: p,
      Source: point.source,
      PointRevisionUUID: point.uuid,
      TemplateRevisionUUID: revisionUuid,
      ModuleName: modules.get(revisionUuid) ?? "Module",
      Section: p.section,
    }
  })
}
export function controlGroup(point: ICloudRegulatoryControlPoint) {
  return `${point.ModuleName ?? "Contrôles"} — ${point.Section || "Général"}`
}

export function regulatoryUuid(uuid: UUID | undefined) {
  return uuid?.value?.length === 16 && uuid.value.some((v) => v !== 0) ? pbUUIDToUuid(uuid) : ""
}

// Unknown historical definitions must never be reconstructed from today's catalog.
export function legacyControls(answers: IEN1176AnswerMap): ICloudRegulatoryControlPoint[] {
  return Object.entries(answers).map(([key, answer]) => ({
    AnswerContextUUID: regulatoryUuid(answer.context_uuid) || undefined,
    UUID: regulatoryUuid(answer.control_point_uuid) || key.split("/").slice(-1)[0]!,
    ID: regulatoryUuid(answer.control_point_uuid) || key.split("/").slice(-1)[0]!,
    Text: `Contrôle historique · ${regulatoryUuid(answer.control_point_uuid)}`,
    ParentID: "",
    Category: ECloudRegulatoryControlPointCategory.Unknown,
    ReferenceUUID: "",
    ReferenceSection: "Définition historique inconnue",
    Reference: null,
    Section: "Réponses historiques",
    ModuleName: "Version historique inconnue",
  }))
}

export function assignRevision(
  entity: Site | Equipment,
  templateChanged: boolean,
  revision?: string,
) {
  if (templateChanged) Message.setField(entity, 26, undefined)
  if (revision) entity.regulatory_template_revision_uuid = uuidToPbUUID(revision)
}
